import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Alert,
  Box,
  Button,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  type SelectChangeEvent,
} from '@mui/material';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { PAGE_SIZE_OPTIONS, PAGE_SIZES } from '@/constants/pagination';
import CloseIcon from '@mui/icons-material/Close';
import StyledButton from '@/components/common/styledButton';
import { useState, useCallback, useRef, useEffect } from 'react';
import {
  accountsApi,
  skipViewingInvestorTagForAccountsQuery,
  useUpdateAccountTagsMutation,
  AccountGroupCategory,
} from '@/store/accountsApi';
import { AccountBase } from '@/models/AccountViewModel';
import { useLocalization } from '@/hooks/useLocalization';
import {
  accountGroupTagStorageKey,
  applyAccountGroupTagsToAccountPayload,
  setAccountGroupCategoryAssignment,
} from '@/utils/accountGroupTagAssignments';

interface AssignAccountSegmentsModalProps {
  open: boolean;
  onClose: () => void;
  accountGroup: AccountGroupCategory;
}

interface AccountWithSegment extends AccountBase {
  AssignedSegmentId?: number;
  AssignedSegmentName?: string;
}

interface UnsavedChangesDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const UnsavedChangesDialog = ({
  open,
  onClose,
  onConfirm,
}: UnsavedChangesDialogProps) => {
  const { t } = useLocalization();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
    >
      <DialogTitle>{t('assignSegments.unsavedChanges')}</DialogTitle>
      <DialogContent>
        <Typography>{t('assignSegments.unsavedChangesMessage')}</Typography>
      </DialogContent>
      <DialogActions>
        <Button
          size='large'
          variant='outlined'
          onClick={onClose}
        >
          {t('cancel')}
        </Button>
        <Button
          size='large'
          onClick={onConfirm}
          color='error'
          variant='outlined'
        >
          {t('assignSegments.closeWithoutSaving')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SegmentSelectCell = ({
  value,
  segments,
  onChange,
}: {
  value?: number;
  segments: { InvestorTagId: number; Name: string }[];
  onChange: (segmentId: number | undefined) => void;
}) => {
  const { t } = useLocalization();

  return (
    <FormControl
      fullWidth
      size='small'
    >
      <Select<number | ''>
        value={value ?? ''}
        onChange={(e: SelectChangeEvent<number | ''>) => {
          const nextValue = e.target.value;
          onChange(nextValue === '' ? undefined : nextValue);
        }}
        displayEmpty
        sx={{
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: '1px solid #d1d5db',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: '1px solid #2563eb',
          },
        }}
      >
        <MenuItem value=''>
          <em>{t('assignSegments.noSegment')}</em>
        </MenuItem>
        {segments.map((segment) => (
          <MenuItem
            key={segment.InvestorTagId}
            value={segment.InvestorTagId}
          >
            {segment.Name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export const AssignAccountSegmentsModal = ({
  open,
  onClose,
  accountGroup,
}: AssignAccountSegmentsModalProps) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [accountsWithSegments, setAccountsWithSegments] = useState<
    AccountWithSegment[]
  >([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'success' | 'error' | null>(
    null,
  );
  const originalAccountsMap = useRef<Map<number, AccountWithSegment>>(
    new Map(),
  );

  const {
    data: accountsData,
    isLoading,
    isError,
  } = accountsApi.useGetAccountsQuery({
    showClosed: false,
    ...skipViewingInvestorTagForAccountsQuery,
  });
  const [updateAccountTags, { isLoading: isSaving }] =
    useUpdateAccountTagsMutation();
  // Initialize accounts with current segment assignments
  useEffect(() => {
    const records = accountsData?.Records;
    if (!records) {
      return;
    }

    const accountsMap = new Map<number, AccountWithSegment>();
    const key = accountGroupTagStorageKey({
      categoryId: accountGroup.TagCategoryId,
      isAdvisorTag: accountGroup.IsAdvisorTag,
    });
    const accountsWithSegmentData: AccountWithSegment[] = records.map(
      (account) => {
        const currentSegmentName = account.Tags[key] || '';
        const currentSegment = accountGroup.Tags.find(
          (tag) => tag.Name === currentSegmentName,
        );

        const accountWithSegment: AccountWithSegment = {
          ...account,
          AssignedSegmentId: currentSegment?.InvestorTagId,
          AssignedSegmentName: currentSegmentName || undefined,
        };
        accountsMap.set(account.AccountId, structuredClone(accountWithSegment));
        return accountWithSegment;
      },
    );

    setAccountsWithSegments(accountsWithSegmentData);
    originalAccountsMap.current = accountsMap;
    setHasChanges(false);
  }, [accountsData, accountGroup]);

  const checkForChanges = (updatedAccounts: AccountWithSegment[]) => {
    for (const account of updatedAccounts) {
      const originalAccount = originalAccountsMap.current.get(
        account.AccountId,
      );
      if (
        originalAccount &&
        account.AssignedSegmentId !== originalAccount.AssignedSegmentId
      ) {
        return true;
      }
    }
    return false;
  };

  const handleSegmentChange = useCallback(
    (accountId: number, segmentId: number | undefined) => {
      setAccountsWithSegments((prev) => {
        const updatedAccounts = prev.map((account) => {
          if (account.AccountId === accountId) {
            const segment = segmentId
              ? accountGroup.Tags.find((tag) => tag.InvestorTagId === segmentId)
              : undefined;
            return {
              ...account,
              AssignedSegmentId: segmentId,
              AssignedSegmentName: segment?.Name,
            };
          }
          return account;
        });

        setHasChanges(checkForChanges(updatedAccounts));
        setSaveStatus(null); // Clear save status on new change
        return updatedAccounts;
      });
    },
    [accountGroup],
  );

  const handleClose = () => {
    if (hasChanges) {
      setShowUnsavedDialog(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowUnsavedDialog(false);
    setAccountsWithSegments(Array.from(originalAccountsMap.current.values()));
    setHasChanges(false);
    onClose();
  };

  const handleSave = async () => {
    setSaveStatus(null);
    try {
      const savePromises = accountsWithSegments.map(async (account) => {
        const originalAccount = originalAccountsMap.current.get(
          account.AccountId,
        );

        // Only save if the segment has changed from the original state
        if (
          originalAccount &&
          account.AssignedSegmentId !== originalAccount.AssignedSegmentId
        ) {
          const segment = account.AssignedSegmentId
            ? accountGroup.Tags.find(
                (tag) => tag.InvestorTagId === account.AssignedSegmentId,
              )
            : undefined;

          const updatedTags = setAccountGroupCategoryAssignment(
            originalAccount.Tags,
            accountGroup.TagCategoryId,
            segment?.Name,
            { isAdvisorTag: accountGroup.IsAdvisorTag },
          );

          const {
            AssignedSegmentId: _assignedSegmentId,
            AssignedSegmentName: _assignedSegmentName,
            ...accountWithoutSegmentFields
          } = originalAccount;
          const accountToSave: Record<string, unknown> = {
            ...accountWithoutSegmentFields,
          };
          applyAccountGroupTagsToAccountPayload(accountToSave, updatedTags);

          await updateAccountTags(
            accountToSave as unknown as AccountBase,
          ).unwrap();
        }
      });

      await Promise.all(savePromises);

      // After successful save, update the original state to the new, saved state
      const newOriginalAccountsMap = new Map<number, AccountWithSegment>();
      accountsWithSegments.forEach((account) => {
        newOriginalAccountsMap.set(account.AccountId, structuredClone(account));
      });
      originalAccountsMap.current = newOriginalAccountsMap;

      setHasChanges(false);
      setSaveStatus('success');
    } catch (error) {
      console.error('Failed to save account segment assignments:', error);
      setSaveStatus('error');
    }
  };

  const columns: GridColDef<AccountWithSegment>[] = [
    {
      field: 'Name',
      headerName: t('grid.accountName'),
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', py: 1.5 }}>
          <Typography>{params.row.Name}</Typography>
          <Typography variant='caption'>
            {params.row.InstitutionName}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'AccountNumber',
      headerName: t('grid.accountNumber'),
      flex: 0.8,
    },
    {
      field: 'AssignedSegmentName',
      headerName: t('assignSegments.segmentTitle'),
      flex: 1,
      renderCell: (params) => (
        <SegmentSelectCell
          value={params.row.AssignedSegmentId}
          segments={accountGroup.Tags}
          onChange={(segmentId) => {
            handleSegmentChange(params.row.AccountId, segmentId);
          }}
        />
      ),
    },
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth='lg'
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography
            variant='h5'
            sx={{ fontWeight: 600, fontSize: '1.25rem' }}
          >
            {t('assignSegments.title', { groupName: accountGroup.Name })}
          </Typography>
          <IconButton
            onClick={handleClose}
            aria-label={t('close')}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {saveStatus === 'success' && (
            <Alert
              severity='success'
              sx={{ mb: 2 }}
            >
              {t('assignSegments.saveSuccess')}
            </Alert>
          )}
          {saveStatus === 'error' && (
            <Alert
              severity='error'
              sx={{ mb: 2 }}
            >
              {t('assignSegments.saveError')}
            </Alert>
          )}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
              <CircularProgress />
            </Box>
          ) : isError ? (
            <Alert severity='error'>{t('assignSegments.loadError')}</Alert>
          ) : (
            <>
              <Typography sx={{ my: 3 }}>
                {t('assignSegments.description', {
                  groupName: accountGroup.Name,
                })}
              </Typography>

              <DataGridPremium
                rows={accountsWithSegments}
                columns={columns}
                getRowId={(row) => row.AccountId}
                autoHeight
                pagination
                hideFooter={accountsWithSegments.length <= 10}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: PAGE_SIZES.SMALL },
                  },
                }}
                pageSizeOptions={[...PAGE_SIZE_OPTIONS.MODAL]}
                disableColumnMenu
                disableRowSelectionOnClick
                getRowHeight={() => 'auto'}
                sx={{
                  border: 0,
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: theme.palette.grey[100],
                    borderBottom: 'none',
                  },
                  '& .MuiDataGrid-cell': {
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '& .MuiDataGrid-row': {
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  },
                  '& .MuiDataGrid-virtualScrollerContent': {
                    overflow: 'unset',
                  },
                }}
              />
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Box>
            {hasChanges && (
              <Typography
                variant='body2'
                color='text.secondary'
              >
                {t('assignSegments.unsavedChangesWarning')}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <StyledButton
              size='large'
              variant='outlined'
              onClick={handleClose}
            >
              {t('cancel')}
            </StyledButton>
            <StyledButton
              size='large'
              variant='contained'
              onClick={() => {
                void handleSave();
              }}
              disabled={!hasChanges || isSaving}
            >
              {isSaving ? <CircularProgress size={16} /> : t('saveChanges')}
            </StyledButton>
          </Box>
        </DialogActions>
      </Dialog>

      <UnsavedChangesDialog
        open={showUnsavedDialog}
        onClose={() => {
          setShowUnsavedDialog(false);
        }}
        onConfirm={handleConfirmClose}
      />
    </>
  );
};

export default AssignAccountSegmentsModal;
